import React, { useEffect, useState } from "react";
import { API_ORIGIN, authFetch } from "../config/apiConfig";
import { canDelete } from "../utils/permissions";

const API_BASE = API_ORIGIN;

// Same icon set as UsersList.jsx's Action column (no shared icons module
// in this project yet -- each management page defines its own small copy,
// matching the existing convention). Kept visually identical so Role /
// Department / Company / Users all use the same eye / pencil / trash
// glyphs inside the shared .icon-btn circles from appLayout.css.
const EyeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 20h9" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="10" y1="11" x2="10" y2="17" strokeLinecap="round" />
    <line x1="14" y1="11" x2="14" y2="17" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// TEMP: this page was showing a blank white screen for the user in
// production -- something in DepartmentManagementImpl below throws at
// runtime, but the exact error hasn't been diagnosed yet (would need
// the browser console output). Per request, swapping the real page out
// for this placeholder for now rather than shipping a blank screen.
// DepartmentManagementImpl and everything it depends on is untouched
// below -- once the console error is known and fixed, restore this by
// renaming DepartmentManagementImpl back to DepartmentManagement and
// deleting this wrapper (and the "export default" above
// DepartmentManagementImpl).
export default function DepartmentManagement() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body text-center py-5">
        <i className="ti ti-rocket fs-1 text-muted d-block mb-3" />
        <h5 className="text-muted mb-0">This Page is Part of next release</h5>
      </div>
    </div>
  );
}

function DepartmentManagementImpl() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departmentName, setDepartmentName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null); // { type: "success"|"error", text }

  // Inline edit-in-place, same pattern for every row: click the pencil,
  // that row's name cell turns into a text input with save/cancel
  // buttons swapped in for view/edit/delete, matching the reference
  // layout's Action column without needing a separate edit page/modal.
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [viewing, setViewing] = useState(null); // department object being viewed, or null

  const loadDepartments = () => {
    setLoading(true);
    authFetch(`${API_BASE}/departments`)
      .then((res) => res.json())
      .then((data) => setDepartments(Array.isArray(data) ? data : []))
      .catch(() => setMessage({ type: "error", text: "Could not load departments." }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDepartments();
  }, []);

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!departmentName.trim()) {
      setMessage({ type: "error", text: "Please enter a department name." });
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/departments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ DepartmentName: departmentName.trim() }),
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error || "Failed to create department." });
        return;
      }

      setMessage({ type: "success", text: "Data successfully added." });
      setDepartmentName("");
      loadDepartments();
    } catch (err) {
      setMessage({ type: "error", text: "Network error while creating the department." });
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (d) => {
    setMessage(null);
    setEditingId(d.DepartmentID);
    setEditingName(d.DepartmentName);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveEdit = async (id) => {
    if (!editingName.trim()) {
      setMessage({ type: "error", text: "Please enter a department name." });
      return;
    }
    setSavingEdit(true);
    try {
      const res = await authFetch(`${API_BASE}/departments/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ DepartmentName: editingName.trim() }),
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error || "Failed to update department." });
        return;
      }

      setMessage({ type: "success", text: "Data successfully updated." });
      setEditingId(null);
      setEditingName("");
      loadDepartments();
    } catch (err) {
      setMessage({ type: "error", text: "Network error while updating the department." });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Delete "${d.DepartmentName}"? This can't be undone.`)) return;

    setMessage(null);
    setDeletingId(d.DepartmentID);
    try {
      const res = await authFetch(`${API_BASE}/departments/${d.DepartmentID}`, { method: "DELETE" });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error || "Failed to delete department." });
        return;
      }

      setMessage({ type: "success", text: "Department deleted." });
      loadDepartments();
    } catch (err) {
      setMessage({ type: "error", text: "Network error while deleting the department." });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container-fluid mt-4" style={{ maxWidth: 900 }}>
      <h4 className="master-page-title">Departments</h4>

      <div className="master-toolbar">
        <label htmlFor="departmentName" className="master-toolbar-label">
          Department
        </label>
        <form className="master-toolbar-row" onSubmit={handleAddDepartment}>
          <input
            id="departmentName"
            type="text"
            className="master-input"
            placeholder="e.g. Technical Department"
            value={departmentName}
            onChange={(e) => setDepartmentName(e.target.value)}
          />
          <button type="submit" className="master-add-btn" disabled={submitting}>
            {submitting ? "ADDING..." : "ADD"}
          </button>
        </form>

        {message && (
          <p className={`master-msg ${message.type === "success" ? "master-msg-success" : "master-msg-error"}`}>
            {message.text}
          </p>
        )}
      </div>

      <div className="master-table-wrap">
        <table className="master-table">
          <thead>
            <tr>
              <th>Department ID</th>
              <th>Department Name</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="text-center">Loading...</td>
              </tr>
            ) : departments.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center">No departments yet.</td>
              </tr>
            ) : (
              departments.map((d) => (
                <tr key={d.DepartmentID}>
                  <td>{d.DepartmentID}</td>
                  <td>
                    {editingId === d.DepartmentID ? (
                      <input
                        type="text"
                        className="master-edit-input"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        autoFocus
                      />
                    ) : (
                      d.DepartmentName
                    )}
                  </td>
                  <td>
                    <div className="project-actions">
                      {editingId === d.DepartmentID ? (
                        <>
                          <button
                            className="icon-btn"
                            title="Save"
                            onClick={() => saveEdit(d.DepartmentID)}
                            disabled={savingEdit}
                          >
                            <CheckIcon />
                          </button>
                          <button className="icon-btn" title="Cancel" onClick={cancelEdit} disabled={savingEdit}>
                            <XIcon />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="icon-btn" title="View" onClick={() => setViewing(d)}>
                            <EyeIcon />
                          </button>
                          <button className="icon-btn" title="Edit" onClick={() => startEdit(d)}>
                            <EditIcon />
                          </button>
                          {/* Super Admin only per the Role Matrix -- Admin's
                              Master cell is Add-Edit, no Delete. */}
                          {canDelete("Master") && (
                            <button
                              className="icon-btn"
                              title="Delete"
                              onClick={() => handleDelete(d)}
                              disabled={deletingId === d.DepartmentID}
                            >
                              <TrashIcon />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {viewing && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000
          }}
          onClick={() => setViewing(null)}
        >
          <div
            style={{ background: "#fff", borderRadius: 8, padding: "24px 28px", minWidth: 320, boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 style={{ margin: "0 0 16px", color: "#154749", fontWeight: 700 }}>Department Details</h5>
            <p style={{ margin: "0 0 8px", fontSize: 14 }}><strong>Department ID:</strong> {viewing.DepartmentID}</p>
            <p style={{ margin: "0 0 20px", fontSize: 14 }}><strong>Department Name:</strong> {viewing.DepartmentName}</p>
            <div style={{ textAlign: "right" }}>
              <button type="button" className="master-add-btn" style={{ padding: "8px 20px" }} onClick={() => setViewing(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}